/**
 * The little Markdown the agent puts inside a card's text fields.
 *
 * Card payloads are JSON, so their strings are meant to be plain — but the
 * agent writes `**39.7% Advocacy Gap**` and `` `p < 0.05` `` into them all the
 * same, and shown as written the asterisks and backticks reach the screen.
 * Bold becomes bold; a backticked span loses its backticks (a card has no
 * use for a code pill). Anything else is left as text.
 */

import React from "react";
import PropTypes from "prop-types";

const MARKS = /(\*\*[^*]+\*\*|__[^_]+__|`[^`]+`)/g;

/**
 * A significance marker or test statistic: `p < 0.05`, `** p < 0.01`, `ns`,
 * `z = +2.80`, `n = 4,931`, `PTP p < 0.05`, `±Δ pts`. Identifiers such as
 * `brand_id` or `selection.set_brands` match none of these.
 */
export const looksLikeStatistic = (text) =>
  /^\s*\*{0,3}\s*(?:PTP\s+)?(?:p\s*[<>=≤≥]|ns\b|n\s*=|z\s*=|±?Δ)/i.test(text) ||
  /\b[pzn]\s*[<>=≤≥]\s*[-+−]?\d/i.test(text);

/** The same text with its marks removed, for parsing or for a tooltip. */
export const stripInline = (text) =>
  typeof text === "string" ? text.replace(/\*\*|__|`/g, "") : text;

const InlineText = ({ text }) => {
  if (typeof text !== "string") return text ?? null;
  return text.split(MARKS).map((piece, i) => {
    if (/^(\*\*|__).+\1$/.test(piece)) return <strong key={i}>{piece.slice(2, -2)}</strong>;
    if (/^`.+`$/.test(piece)) return <React.Fragment key={i}>{piece.slice(1, -1)}</React.Fragment>;
    return piece ? <React.Fragment key={i}>{piece}</React.Fragment> : null;
  });
};

InlineText.propTypes = { text: PropTypes.string };

export default InlineText;
